/**
 * Sales Analytics Service
 *
 * API service layer for sales analytics endpoints.
 * Integrates with useAuthenticatedApi hook for automatic authentication.
 */

import type { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import type {
  SalesOverview,
  SalespersonDetails,
  CustomerAnalytics,
  CustomerDetailData,
  TopProjects,
  MonthlyTrendData,
  TopCustomersData,
  CustomersOverviewData
} from "@/shared/types/sales-analytics";

/**
 * Sales Analytics API Service
 * Uses authenticated API client for all requests
 */
export class SalesAnalyticsService {
  constructor(private api: ReturnType<typeof useAuthenticatedApi>) {}

  /**
   * Get sales overview with aggregated metrics
   * @param params - Optional period, comparison flags, and preview parameters
   */
  async getSalesOverview(params?: {
    period?: string; // YYYY-MM format
    includeComparison?: boolean;
    previewAs?: string; // Admin preview mode: view as specific salesperson
  }): Promise<SalesOverview> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.includeComparison) {
      searchParams.set("includeComparison", "true");
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    const url = `/api/sales/overview${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch sales overview");
    }

    const result = await response.json() as { data: SalesOverview };
    return result.data;
  }

  /**
   * Get detailed salesperson analytics
   * @param salespersonId - Salesperson gUId
   * @param params - Optional period parameter
   */
  async getSalespersonDetails(
    salespersonId: string,
    params?: { period?: string }
  ): Promise<SalespersonDetails> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    const url = `/api/sales/salespersons/${salespersonId}${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch salesperson details");
    }

    const result = await response.json() as { data: SalespersonDetails };
    return result.data;
  }

  /**
   * Get customer portfolio analytics
   * @param params - Salesperson ID and optional filters
   */
  async getCustomerAnalytics(params: {
    salespersonId: string;
    period?: string;
    limit?: number;
    offset?: number;
  }): Promise<CustomerAnalytics> {
    const searchParams = new URLSearchParams();
    searchParams.set("salespersonId", params.salespersonId);

    if (params.period) {
      searchParams.set("period", params.period);
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params.offset !== undefined) {
      searchParams.set("offset", params.offset.toString());
    }

    const url = `/api/sales/customers?${searchParams}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch customer analytics");
    }

    const result = await response.json() as { data: CustomerAnalytics };
    return result.data;
  }

  /**
   * Get customers overview (global analytics across all salespersons)
   * @param params - Optional period, limit, and preview parameters
   */
  async getCustomersOverview(params?: {
    period?: string;
    limit?: number;
    previewAs?: string; // Admin preview mode: view as specific salesperson
  }): Promise<CustomersOverviewData> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.limit) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    const url = `/api/sales/customers/overview${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch customers overview");
    }

    const result = await response.json() as { data: CustomersOverviewData };
    return result.data;
  }

  /**
   * Get detailed customer analytics for an individual customer
   * @param customerId - Customer code
   * @param params - Optional period and trend flags
   */
  async getCustomerDetails(
    customerId: string,
    params?: { period?: string; includeMonthlyTrend?: boolean }
  ): Promise<CustomerDetailData> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.includeMonthlyTrend) {
      searchParams.set("includeMonthlyTrend", "true");
    }

    const url = `/api/sales/customer/${customerId}${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch customer details");
    }

    const result = await response.json() as { data: CustomerDetailData };
    return result.data;
  }

  /**
   * Get top projects by revenue
   * @param params - Optional period and limit
   */
  async getTopProjects(params?: {
    period?: string;
    limit?: 5 | 10 | 20 | 50;
    previewAs?: string;
  }): Promise<TopProjects> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.limit) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    const url = `/api/sales/projects/top${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch top projects");
    }

    const result = await response.json() as { data: TopProjects };
    return result.data;
  }

  /**
   * Get monthly sales trend data for the year
   * @param year - Year for trend data (defaults to current year)
   * @param metric - Metric type: 'revenue' (paid amounts) or 'sales' (invoice amounts)
   */
  async getMonthlySalesTrend(year?: number, metric?: 'revenue' | 'sales', previewAs?: string): Promise<MonthlyTrendData> {
    const currentYear = year || new Date().getFullYear();
    const metricParam = metric || 'revenue';
    const searchParams = new URLSearchParams({ year: String(currentYear), metric: metricParam });
    if (previewAs) searchParams.set("previewAs", previewAs);
    const url = `/api/sales/monthly-trend?${searchParams.toString()}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch monthly sales trend");
    }

    const result = await response.json() as { data: MonthlyTrendData };
    return result.data;
  }

  /**
   * Get top spending customers
   * @param params - Optional period and limit
   */
  async getTopCustomers(params?: {
    period?: string;
    limit?: number;
    previewAs?: string;
  }): Promise<TopCustomersData> {
    const searchParams = new URLSearchParams();

    if (params?.period) {
      searchParams.set("period", params.period);
    }

    if (params?.limit) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params?.previewAs) {
      searchParams.set("previewAs", params.previewAs);
    }

    const url = `/api/sales/top-customers${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to fetch top customers");
    }

    const result = await response.json() as { data: TopCustomersData };
    return result.data;
  }

  /**
   * Export data to CSV format
   * @param endpoint - Which data to export (overview, salesperson, customers, projects)
   * @param params - Query parameters for the export
   */
  async exportToCSV(
    endpoint: 'overview' | 'salesperson' | 'customers' | 'projects',
    params?: Record<string, string>
  ): Promise<string> {
    const searchParams = new URLSearchParams(params);
    searchParams.set("format", "csv");

    let url: string;
    switch (endpoint) {
      case 'overview':
        url = `/api/sales/overview?${searchParams}`;
        break;
      case 'salesperson':
        url = `/api/sales/salespersons/${params?.id}?${searchParams}`;
        break;
      case 'customers':
        url = `/api/sales/customers?${searchParams}`;
        break;
      case 'projects':
        url = `/api/sales/projects/top?${searchParams}`;
        break;
    }

    const response = await this.api.request(url);

    if (!response.ok) {
      const error = await response.json() as { error?: string };
      throw new Error(error.error || "Failed to export data");
    }

    return await response.text();
  }
}

